/**
 * P1-S2 — discreet mode.
 *
 * When on, the browser tab reads "Notes" with a neutral glyph and catalog /
 * position images stay hidden. The flag lives in `localStorage.hc_discreet`;
 * `useDiscreet()` is the hook other screens use to hide their own images.
 */
import { useEffect, useSyncExternalStore } from 'react';

export const DISCREET_STORAGE_KEY = 'hc_discreet';
export const DISCREET_CHANGE_EVENT = 'hc:discreet-change';
export const DISCREET_TITLE = 'Notes';

/** A plain document glyph: nothing that says couples, hearts or games. */
export const DISCREET_FAVICON =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">' +
      '<rect width="32" height="32" rx="7" fill="#d4a017"/>' +
      '<rect x="8" y="7" width="16" height="2.5" rx="1.25" fill="#fff"/>' +
      '<rect x="8" y="13" width="16" height="2.5" rx="1.25" fill="#fff"/>' +
      '<rect x="8" y="19" width="10" height="2.5" rx="1.25" fill="#fff"/>' +
      '</svg>'
  );

export function getDiscreet(): boolean {
  try {
    return window.localStorage.getItem(DISCREET_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setDiscreet(enabled: boolean): void {
  try {
    window.localStorage.setItem(DISCREET_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // Storage blocked: the change still applies for this page via the event.
  }
  try {
    window.dispatchEvent(new Event(DISCREET_CHANGE_EVENT));
  } catch {
    // No window: nothing to notify.
  }
}

function subscribe(onChange: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === DISCREET_STORAGE_KEY) onChange();
  };
  window.addEventListener(DISCREET_CHANGE_EVENT, onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(DISCREET_CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onStorage);
  };
}

/** Reactive `hc_discreet` flag. Hide catalog / position images when true. */
export function useDiscreet(): boolean {
  return useSyncExternalStore(subscribe, getDiscreet, () => false);
}

// ─── Tab disguise (title + favicon) ───────────────────────────────────────────

interface Saved {
  title: string;
  iconHref: string | null;
  iconType: string | null;
  createdIcon: HTMLLinkElement | null;
}

let holders = 0;
let saved: Saved | null = null;

function findIconLink(): HTMLLinkElement | null {
  return document.head.querySelector<HTMLLinkElement>('link[rel~="icon"]');
}

function applyDisguise(): void {
  let link = findIconLink();
  let createdIcon: HTMLLinkElement | null = null;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
    createdIcon = link;
  }
  saved = {
    title: document.title,
    iconHref: link.getAttribute('href'),
    iconType: link.getAttribute('type'),
    createdIcon,
  };
  document.title = DISCREET_TITLE;
  link.setAttribute('type', 'image/svg+xml');
  link.setAttribute('href', DISCREET_FAVICON);
}

function restoreDisguise(): void {
  if (!saved) return;
  const { title, iconHref, iconType, createdIcon } = saved;
  saved = null;
  document.title = title;
  if (createdIcon) {
    createdIcon.remove();
    return;
  }
  const link = findIconLink();
  if (!link) return;
  if (iconHref === null) link.removeAttribute('href');
  else link.setAttribute('href', iconHref);
  if (iconType === null) link.removeAttribute('type');
  else link.setAttribute('type', iconType);
}

/**
 * Shows the neutral title and favicon while `active`. Several callers can
 * hold it at once (discreet mode and the decoy page); the original title and
 * icon come back when the last one lets go.
 */
export function useTabDisguise(active: boolean): void {
  useEffect(() => {
    if (!active) return undefined;
    if (holders === 0) applyDisguise();
    holders += 1;
    return () => {
      holders -= 1;
      if (holders === 0) restoreDisguise();
    };
  }, [active]);
}
